import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

type Props = {
    filters: any;
    data: any[];
};

export default function LeadsInsightsChartAll(props: Props) {
    const location = useLocation();
    const path = location.pathname;
    
    const isDashboard = path === '/';

    const chartDiv = 'leads-insights-chart-mix';

    useEffect(() => {
        if (!props.data) {
            return;
        }

        const root = am5.Root.new(chartDiv);

        const customTheme = am5.Theme.new(root);

        customTheme.rule('Label').setAll({
            fill: am5.color(0x262626),
            fontSize: isDashboard ? '0.85rem' : '0.969rem',
            fontFamily: 'Poppins'
        });

        root.setThemes([am5themes_Animated.new(root), customTheme]);
        root._logo.dispose();

        const chart = root.container.children.push(am5xy.XYChart.new(root, {
            panX: false,
            panY: false,
            wheelX: 'panX',
            wheelY: 'zoomX',
            paddingLeft: 0,
            paddingRight: 0,
            layout: root.verticalLayout
        }));

        if (!isDashboard) {
            chart.set('scrollbarX', am5.Scrollbar.new(root, {
                orientation: 'horizontal',
                height: 10,
                width: am5.percent(99),
                marginBottom: 30
            }));

            const scrollbarX = chart.get('scrollbarX');

            scrollbarX.get('background').setAll({
                fill: am5.color(0x757474)
            });
        }

        const now = new Date();

        const data = props.data.map((item: any) => {
            let date = new Date();
            date.setHours(0,0,0,0);
            date.setDate(1);

            let lastColumn = false;
            let lastLine = false;

            switch (props.filters.DataPoint) {
                case 'month':
                    date.setFullYear(item.Year);
                    date.setMonth(item.Month - 1);
                    root.dateFormatter.set('dateFormat', 'MMM yyyy');
                    lastColumn = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
                    lastLine = date.getMonth() === now.getMonth() - 1;
                    break;
                case 'week':
                    date.setFullYear(item.Year);
                    date.setMonth(0);
                    date.setDate(item.Week * 7);
                    root.dateFormatter.set('dateFormat', 'MMM dt, yyyy');
                    break;
                case 'day':
                    date = new Date(item.EventDate);
                    root.dateFormatter.set('dateFormat', 'MMM dt, yyyy');
                    break;
            }

            const columnSettings = lastColumn ? {
                fillOpacity: 0.5
            } : {};

            const strokeSettings = lastLine ? {
                stroke: am5.color('#C3B1E1'),
                fill: am5.color('#C3B1E1')
            } : {
                stroke: am5.color('#C3B1E1'),
                fill: lastColumn ? am5.color('#fff') : am5.color('#C3B1E1')
            };

            return {
                ...item,
                EventDate: date,
                ColumnSettings: columnSettings,
                StrokeSettings: strokeSettings
            };
        }).sort((a, b) => a.EventDate < b.EventDate ? -1 : 1);

        // x line
        const xRenderer = am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
            strokeOpacity: 1,
            strokeWidth: 1,
            stroke: am5.color(0xd0d0d0)
        });

        // x grid
        xRenderer.grid.template.setAll({ location: 1, strokeWidth: 1, stroke: am5.color(0xeaeaea), strokeOpacity: 1 });

        // x labels
        xRenderer.labels.template.setAll({
            textAlign: 'center',
            oversizedBehavior: 'wrap',
            fontSize: isDashboard ? '0.82rem' : '0.9rem',
            breakWords: isDashboard ? true : false,
            maxWidth: isDashboard ? 50 : 100,
            paddingTop: isDashboard ? 6 : 12,
            lineHeight: 1
        });

        const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
            categoryField: 'EventDate',
            renderer: xRenderer,
            tooltip: am5.Tooltip.new(root, {})
        }));

        // y line
        const yRenderer = am5xy.AxisRendererY.new(root, {
            minorGridEnabled: false,
            strokeOpacity: 1,
            strokeWidth: 1,
            stroke: am5.color(0xd0d0d0)
        });

        // y grid
        yRenderer.grid.template.setAll({ location: 1, strokeWidth: 1, stroke: am5.color(0xeaeaea), strokeOpacity: 1 });

        // y labels
        yRenderer.labels.template.setAll({
            fontSize: isDashboard ? '0.82rem' : '0.9rem',
            paddingRight: isDashboard ? 6 : 12
        });

        const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            renderer: yRenderer,
            maxPrecision: 0
        }));

        xAxis.data.setAll(data);

        // tooltips 
        const tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            getStrokeFromSprite: false,
            autoTextColor: true,
            getLabelFillFromSprite: true,
            paddingTop: 5,
        });
        tooltip.get('background').setAll({
            fill: am5.color(0xffffff),
            fillOpacity: 1,
            stroke: am5.color(0xd0d0d0)
        });

        const legend = chart.children.push(am5.Legend.new(root, {
            centerX: isDashboard ? am5.p0 : am5.p50,
            x: isDashboard ? 30 : am5.p50,
            y: am5.percent(90),
            //y: isDashboard ? am5.percent(90) : am5.percent(95),
            layout: am5.GridLayout.new(root, {
                maxColumns: 3,
                fixedWidthGrid: false
            }),
            marginTop: isDashboard ? 0 : 20
        }));

        const makeSeries = (name: any, fieldName: any, color: string) => {
            const series = chart.series.push(am5xy.ColumnSeries.new(root, {
                name: name,
                stacked: true,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: fieldName,
                categoryXField: 'EventDate',
                fill: am5.color(color)
            }));

            series.columns.template.setAll({
                tooltipText: '{name} ({categoryX}): [bold]{valueY}[/]',
                tooltipY: 0,
                templateField: 'ColumnSettings',
            });

            series.data.setAll(data);
            series.appear();

            legend.data.push(series);
        };

        const series2 = chart.series.push(am5xy.LineSeries.new(root, {
                name: 'Page Views',
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: 'PageViews',
                categoryXField: 'EventDate',
                stroke: am5.color('#C3B1E1')
            })
        );

        series2.strokes.template.setAll({
            strokeWidth: 2,
            templateField: 'StrokeSettings'
        });

        series2.data.setAll(data);

        series2.bullets.push(() => {
            return am5.Bullet.new(root, {
                sprite: am5.Circle.new(root, {
                    strokeWidth: 2,
                    radius: 5,
                    tooltip: tooltip,
                    tooltipText: '{name} ({categoryX}): [bold]{valueY}[/]',
                    tooltipY: -5,
                    templateField: 'StrokeSettings',
                    dx: 0,
                    dy: 0,
                })
            });
        });

        legend.data.push(series2);

        makeSeries('Website Referrals', 'WebsiteReferrals', '#006edb');
        makeSeries('Phone Number Views', 'PhoneNumberViews', '#37415c');
        makeSeries('Email Inquiries', 'EmailInquiries', '#e66b00');
        makeSeries('Brochure Inquiries ', 'BrochureInquiries', '#1d8b80');
        makeSeries('Tour Requests ', 'TourRequests', '#FF6961');

        series2.appear();
        chart.appear(1000, 100);

        return () => root.dispose();
    }, [props.data]);

    return <div id={chartDiv} className="w-full min-h-96"></div>;
}
